import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Lock, Mail, AlertCircle, Calendar, MapPin, User } from 'lucide-react';
import { authService } from '../authService';
import { AUTH_CONFIG } from '../config';

// Message Component with Animation
const Message = ({ message, type = 'info' }) => {
  const bgColor = type === 'error' ? 'bg-red-50' : 
                 type === 'success' ? 'bg-green-50' : 'bg-beige';
  const textColor = type === 'error' ? 'text-red-700' : 
                   type === 'success' ? 'text-green-700' : 'text';
  const Icon = type === 'error' ? AlertCircle : 
              type === 'success' ? CheckCircle : Mail;

  return message ? (
    <motion.div 
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className={`p-4 mb-4 rounded-lg flex items-start ${bgColor}`}
    >
      <Icon className={`mr-2 ${textColor}`} size={20} />
      <p className={textColor}>{message}</p>
    </motion.div>
  ) : null;
};

// VerificationBoxes Component with Animation for OTP
const VerificationBoxes = ({ onComplete }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);
  const [digitsLeft, setDigitsLeft] = useState(6);

  useEffect(() => {
    // Calculate digits left
    const filledDigits = code.filter(digit => digit !== '').length;
    setDigitsLeft(6 - filledDigits);
    
    // Call onComplete when all digits are filled
    if (filledDigits === 6) {
      onComplete(code.join(''));
    }
  }, [code, onComplete]);

  // Handle input change
  const handleChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;
    
    // Create a new array with the updated value
    const newCode = [...code];
    newCode[index] = value.slice(0, 1); // Only take first character
    setCode(newCode);
    
    // Auto-focus next input if a digit was entered
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  // Handle keydown
  const handleKeyDown = (index, e) => {
    // Move to previous input when backspace is pressed on an empty input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
    
    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    
    // Check if pasted content is all digits
    if (/^\d+$/.test(pastedData)) {
      const digits = pastedData.split('').slice(0, 6);
      const newCode = [...code];
      
      digits.forEach((digit, index) => {
        if (index < 6) newCode[index] = digit;
      });
      
      setCode(newCode);
      
      // Focus the appropriate input after paste
      if (digits.length < 6) {
        inputRefs.current[digits.length].focus();
      } else {
        inputRefs.current[5].focus();
      }
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      <div className="flex space-x-2 md:space-x-4 justify-center mb-6">
        {code.map((digit, index) => (
          <motion.div
            key={index}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: index * 0.08 }}
            className={`w-12 h-16 rounded-lg flex items-center justify-center ${digit ? 'border-2 border-accent-default shadow-md' : 'border-2 border-gray-200'}`}
          >
            <input
              ref={el => inputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              className="w-full h-full text-center text-2xl bg-transparent border-none outline-none"
            />
          </motion.div>
        ))}
      </div>
      
      <motion.div 
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="bg-gray-100 p-3 rounded-lg text-center mt-4"
      >
        <p className="text-gray-600 text-lg">{digitsLeft} caratteri rimanenti</p>
      </motion.div>
    </motion.div>
  );
};

const LoginRegistrationForm = () => {
  const { user, signIn, refreshAuthState } = useAuth();
  
  // Initial state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [province, setProvince] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [loading, setLoading] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  
  // Contract acceptance
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Form visibility states
  const [showEmailForm, setShowEmailForm] = useState(true);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [showOtpForm, setShowOtpForm] = useState(false);
  
  // Check if user is already logged in
  useEffect(() => {
    if (user) {
      setMessage("Hai effettuato l'accesso!");
      setMessageType('success');
      setShowEmailForm(false);
      setShowLoginForm(false);
      setShowRegistrationForm(false);
      setShowOtpForm(false);
    }
  }, [user]);
  
  // Animation variants
  const formVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.2 } }
  };
  
  const buttonVariants = {
    initial: { scale: 0.95, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    tap: { scale: 0.98 },
    hover: { scale: 1.02 }
  };
  
  // Send OTP to the email
  const sendOtp = async (email) => {
    try {
      // Email validation
      if (!email || !email.includes('@') || !email.includes('.')) {
        return {
          error: {
            message: "Invalid email format"
          }
        };
      }
      
      // API call for OTP
      const response = await fetch(`${AUTH_CONFIG.apiUrl}/auth/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error("OTP API Error:", errorData);
        return {
          error: {
            message: errorData.detail || "Unable to send verification code"
          }
        };
      }
      
      const data = await response.json();
      return { data };
      
    } catch (error) {
      console.error("Exception during OTP sending:", error);
      return {
        error: {
          message: error.message || "Unable to send verification code"
        }
      };
    }
  };
  
  // Verify OTP
  const verifyOtp = async (email, otpCode) => {
    try {
      const response = await fetch(`${AUTH_CONFIG.apiUrl}/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email: email.trim().toLowerCase(),
          otp_code: otpCode,
          password_bind: true
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.detail || 'Email verification failed' };
      }
      
      const tokenData = await response.json();
      
      // Token data validation
      if (!tokenData || !tokenData.access_token) {
        return { error: 'Invalid token data' };
      }
      
      // Check if password registration is necessary
      const necessaryPasswordRegistration = tokenData.necessary_password_registration || false;
      
      // Save tokens
      authService.saveTokens(tokenData);
      
      return { 
        data: tokenData,
        necessaryPasswordRegistration 
      };
    } catch (error) {
      console.error('Email verification error:', error);
      return { error: 'Verification failed' };
    }
  };
  
  // Check if email exists and has password
  const checkEmailExists = async (email) => {
    try {
      const token = authService.getToken();
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${AUTH_CONFIG.apiUrl}/auth/email-exist?email=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.detail || 'Unable to verify email' };
      }
      
      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Email check error:', error);
      return { error: 'Unable to verify if email exists' };
    }
  };
  

const handleEmailSubmit = async (e) => {
  e.preventDefault();
  
  if (!email || !email.includes('@')) {
    setMessage('Ti preghiamo di inserire un indirizzo email valido!');
    setMessageType('error');
    return;
  }
  
  setLoading(true);
  
  try {
    // Step 1: Check if the user exists and has a password
    const { data, error } = await checkEmailExists(email);
    
    if (error) {
      console.error("Email verification error:", error);
      setMessage(`Errore durante la verifica dell'email: ${error}`);
      setMessageType('error');
      setLoading(false);
      return;
    }
    
    const userExistsInDb = data?.exist || false;
    const userHasPassword = data?.has_password || false;
    
    console.log("User status:", { userExistsInDb, userHasPassword });
    
    // If user exists and has a password, show login form
    if (userExistsInDb && userHasPassword) {
      setMessage('Inserisci la tua password per continuare!');
      setMessageType('info');
      setShowLoginForm(true);
      setShowEmailForm(false);
      setLoading(false);
      return;
    }
    
    // For all other cases - new user or existing user without password
    // Send OTP for verification first
    const otpResult = await sendOtp(email);
    
    if (otpResult.error) {
      setMessage(`Errore: ${otpResult.error.message}`);
      setMessageType('error');
    } else {
      setMessage('Ti abbiamo inviato un codice di verifica. Controlla la tua casella email!');
      setMessageType('info');
      setShowOtpForm(true);
      setShowEmailForm(false);
    }
  } catch (error) {
    console.error('Error in handleEmailSubmit:', error);
    setMessage(`Si è verificato un errore: ${error.message}`);
    setMessageType('error');
  } finally {
    setLoading(false);
  }
};
  // Handle login form submit
// Modified handleLoginSubmit function with redirect to profile page
const handleLoginSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  
  try {
    // Attempt login with email and password
    const { error } = await signIn(email, password);
    
    if (error) {
      setMessage(`Errore di accesso: ${error}`);
      setMessageType('error');
    } else {
      setMessage('Accesso eseguito correttamente!');
      setMessageType('success');
      
      // Refresh auth state
      await refreshAuthState();
      
      // Hide all forms
      setShowLoginForm(false);
      
      // Add a small delay before redirecting to ensure state updates and message is seen
      setTimeout(() => {
        window.location.href = '/profilo';
      }, 1000);
    }
  } catch (error) {
    setMessage(`Errore di accesso: ${error.message}`);
    setMessageType('error');
  } finally {
    setLoading(false);
  }
};
  
  // Handle OTP verification
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Call verifyOtp function
      const { data, error, necessaryPasswordRegistration } = await verifyOtp(email, otpCode);

      if (error) {
        setMessage(`Verifica Fallita: ${error}`);
        setMessageType('error');
      } else {
        // If verification successful, move to registration
        setMessage('Email verificata, completa la tua registrazione!');
        setMessageType('success');
        setShowOtpForm(false);
        setShowRegistrationForm(true);
        
        // Save the token
        if (data && data.access_token) {
          authService.saveTokens(data);
        }
      }
    } catch (error) {
      setMessage(`Error during verification: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle registration form submit
// Modified handleRegistrationSubmit function with redirect
const handleRegistrationSubmit = async (e) => {
  e.preventDefault();
  
  // Validate all required fields
  if (!password || password.length < 8) {
    setMessage('La password deve essere di almeno 8 caratteri');
    setMessageType('error');
    return;
  }
  
  if (!name || !surname) {
    setMessage('Nome e cognome sono richiesti');
    setMessageType('error');
    return;
  }
  
  if (!dateOfBirth) {
    setMessage('La data di nascita è richiesta');
    setMessageType('error');
    return;
  }
  
  if (!province) {
    setMessage('La provincia di residenza è richiesta');
    setMessageType('error');
    return;
  }

  if (!termsAccepted) {
    setMessage('Devi accettare i Termini di Servizio e la Privacy Policy per continuare');
    setMessageType('error');
    return;
  }
  
  setLoading(true);
  
  try {
    // Get token if available
    const token = authService.getToken();
    if (!token) {
      // If no token (from OTP), we need to create a new user
      setMessage('Sessione scaduta. Per favore ricomincia il processo.');
      setMessageType('error');
      setLoading(false);
      setShowEmailForm(true);
      setShowRegistrationForm(false);
      return;
    }
    
    // Update user with registration data
    const response = await fetch(`${AUTH_CONFIG.apiUrl}/auth/user`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: email,
        password: password,
        has_password: true,
        name: name,
        surname: surname,
        date_of_birth: dateOfBirth,
        province: province
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Errore durante la registrazione');
    }
    
    // Get response data
    const updateResponse = await response.json();
    console.log("Registration response:", updateResponse);
    
    // Update auth state
    await refreshAuthState();
    
    setMessage('Registrazione completata con successo!');
    setMessageType('success');
    
    // Hide registration form
    setShowRegistrationForm(false);
    
    // Redirect to profile page after a short delay
    setTimeout(() => {
      window.location.href = '/profilo';
    }, 1000);
    
  } catch (error) {
    setMessage(`Errore durante la registrazione: ${error.message}`);
    setMessageType('error');
  } finally {
    setLoading(false);
  }
};

  // Reset the form state
  const handleReset = () => {
    setEmail('');
    setPassword('');
    setName('');
    setSurname('');
    setDateOfBirth('');
    setProvince('');
    setOtpCode('');
    setMessage('');
    setShowEmailForm(true);
    setShowLoginForm(false);
    setShowRegistrationForm(false);
    setShowOtpForm(false);
    setTermsAccepted(false); 
  };
  
  // Handle forgot password
  const handleForgotPassword = async () => {
    setLoading(true);
    
    try {
      // Request password reset via OTP
      const { error } = await sendOtp(email);
      
      if (error) {
        setMessage(`Error: ${error.message}`);
        setMessageType('error');
      } else {
        setMessage('Controlla la tua casella di posta');
        setMessageType('info');
        setShowLoginForm(false);
        setShowOtpForm(true);
      }
    } catch (error) {
      console.error('Error in handleForgotPassword:', error);
      setMessage(`An error occurred: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };
  
  // Render reset button
  const renderResetButton = () => {
    if (showLoginForm || showRegistrationForm || showOtpForm) {
      return (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          type="button"
          onClick={handleReset}
          className="w-full text-gray-500 text-sm mt-6 hover:text-gray-700 transition-colors"
        >
          Torna al login
        </motion.button>
      );
    }
    return null;
  };
  
  // Email form
  const renderEmailForm = () => {
    return (
      <motion.form 
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={formVariants}
        onSubmit={handleEmailSubmit} 
        className="space-y-4"
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="text-2xl font-bold text-dark mb-6 text-center">Benvenuti!</h2>
          <p className="text-gray-600 mb-6 text-center">Inserisci la tua email per continuare</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <label 
            htmlFor="email" 
            className="block mb-2 font-medium text-gray-700"
          >
            Email:
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-3 text-gray-400" size={18} />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg focus:border-beige-dark focus:ring-0 transition-colors outline-none"
              placeholder="Scrivi it tuo indirizzo email"
            />
          </div>
        </motion.div>

        <motion.button
          variants={buttonVariants}
          initial="initial"
          animate="animate"
          whileTap="tap"
          whileHover="hover"
          disabled={loading}
          type="submit"
          className="w-full py-3 px-6 bg-green text-white rounded-full font-bold shadow-md hover:accent-default transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
              Sto pensando...
            </div>
          ) : 'Continua'}
        </motion.button>
      </motion.form>
    );
  };
  
  // Login form
  const renderLoginForm = () => {
    return (
      <motion.form 
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={formVariants}
        onSubmit={handleLoginSubmit} 
        className="space-y-4"
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="text-2xl font-bold text-beige-dark mb-6 text-center">Bentornati!</h2>
          <p className="text-gray-600 mb-6 text-center">Esegui il login per continuare</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-4"
        >
          <label
            htmlFor="email-display" 
            className="block mb-2 font-medium text-gray-700"
          >
            Email:
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-3 text-gray-400" size={18} />
            <input
              id="email-display"
              type="email"
              value={email}
              readOnly
              className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-600"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <label 
            htmlFor="password" 
            className="block mb-2 font-medium text-gray-700"
          >
            Password:
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 text-gray-400" size={18} />
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg focus:border-accent-default focus:ring-0 transition-colors outline-none"
              placeholder="Scrivi la tua password"
            />
          </div>
        </motion.div>

        <motion.button
          variants={buttonVariants}
          initial="initial"
          animate="animate"
          whileTap="tap"
          whileHover="hover"
          disabled={loading || !password}
          type="submit"
          className="w-full py-3 px-6 bg-beige-dark text-white rounded-full font-bold shadow-md hover:accent-default transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
              Accesso in corso...
            </div>
          ) : 'Login'}
        </motion.button>

        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          type="button"
          onClick={handleForgotPassword}
          disabled={loading}
          className="w-full mt-4 py-3 px-6 bg-transparent text-beige-dark border-2 border-beige-dark rounded-full font-medium hover:bg-beige transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? 'Sto pensando...' : 'Password Dimenticata?'}
        </motion.button>

        {renderResetButton()}
      </motion.form>
    );
  };
  
  // OTP form
  const renderOtpForm = () => {
    return (
      <motion.form 
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={formVariants}
        onSubmit={handleOtpSubmit} 
        className="flex flex-col items-center w-full"
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center mb-6 w-full"
        >
          <h2 className="text-2xl font-bold text-beige-dark mb-2">Verifica la tua email!</h2>
          <p className="text-gray-600">Inserisci il codice di 6 cifre inviato a {email}</p>
        </motion.div>

        <VerificationBoxes 
          onComplete={(code) => setOtpCode(code)}
        />

        <motion.button
          variants={buttonVariants}
          initial="initial"
          animate="animate"
          whileTap="tap"
          whileHover="hover"
          disabled={loading || otpCode.length !== 6}
          type="submit"
          className="w-full py-3 px-6 mt-6 bg-beige-dark text-white rounded-full font-bold shadow-md hover:accent-default transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
              Verifica in corso...
            </div>
          ) : 'Verifica Codice'}
        </motion.button>

        {renderResetButton()}
      </motion.form>
    );
  };
  
  // Registration form
  const renderRegistrationForm = () => {
    return (
      <motion.form 
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={formVariants}
        onSubmit={handleRegistrationSubmit} 
        className="space-y-4"
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="text-2xl font-bold text-beige-dark mb-6 text-center">Completa la tua registrazione</h2>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-4"
        >
          <label 
            htmlFor="email-reg" 
            className="block mb-2 font-medium text-gray-700"
          >
            Email:
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-3 text-gray-400" size={18} />
            <input
              id="email-reg"
              type="email"
              value={email}
              readOnly
              className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-600"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-4"
        >
          <label 
            htmlFor="password-reg" 
            className="block mb-2 font-medium text-gray-700"
          >
            Password:
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 text-gray-400" size={18} />
            <input
              id="password-reg"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg focus:border-accent-default focus:ring-0 transition-colors outline-none"
              placeholder="Crea una password (minimo 8 caratteri)"
              minLength={8}
            />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-4"
          >
            <label 
              htmlFor="name" 
              className="block mb-2 font-medium text-gray-700"
            >
              Nome:
            </label>
            <div className="relative">
              <User className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg focus:border-accent-default focus:ring-0 transition-colors outline-none"
                placeholder="Il tuo nome"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-4"
          >
            <label 
              htmlFor="surname" 
              className="block mb-2 font-medium text-gray-700"
            >
              Cognome:
            </label>
            <div className="relative">
              <User className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
                id="surname"
                type="text"
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
                required
                className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg focus:border-accent-default focus:ring-0 transition-colors outline-none"
                placeholder="Il tuo cognome"
              />
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-4"
        >
          <label 
            htmlFor="dateOfBirth" 
            className="block mb-2 font-medium text-gray-700"
          >
            Data di nascita:
          </label>
          <div className="relative">
            <Calendar className="absolute left-3 top-3 text-gray-400" size={18} />
            <input
              id="dateOfBirth"
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              required
              className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg focus:border-accent-default focus:ring-0 transition-colors outline-none"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.7 }}
          className="mb-6"
        >
          <label 
            htmlFor="province" 
            className="block mb-2 font-medium text-gray-700"
          >
            Provincia di Residenza:
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-3 text-gray-400" size={18} />
            <input
              id="province"
              type="text"
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              required
              className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg focus:border-accent-default focus:ring-0 transition-colors outline-none"
              placeholder=" La tua provincia"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-6"
        >
          <div className="flex items-start">
            <div className="flex items-center h-5 mt-1">
              <input
                id="terms"
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                required
                className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-accent-default"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="terms" className="font-medium text-gray-700">
                Accetto i <a href="/terms" target="_blank" className="text-green hover:underline">Termini di Servizio</a> e la <a href="/privacy" target="_blank" className="text-green hover:underline">Privacy Policy</a>
              </label>
            </div>
          </div>
        </motion.div>

        <motion.button
          variants={buttonVariants}
          initial="initial"
          animate="animate"
          whileTap="tap"
          whileHover="hover"
          disabled={loading || !password || !name || !surname || !dateOfBirth || !province || !termsAccepted}
          type="submit"
          className="w-full py-3 px-6 bg-beige-dark text-white rounded-full font-bold shadow-md hover:accent-default transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
              Ti sto registrando...
            </div>
          ) : 'Completa Registrazione'}
        </motion.button>

        {renderResetButton()}
      </motion.form>
    );
  };
  
  return (
    <div className="w-full max-w-md mx-auto p-6 bg-white rounded-xl shadow-lg">
      <AnimatePresence>
        {message && (
          <Message message={message} type={messageType} />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {showEmailForm && renderEmailForm()}
        {showLoginForm && renderLoginForm()}
        {showOtpForm && renderOtpForm()}
        {showRegistrationForm && renderRegistrationForm()}
      </AnimatePresence>
      
      {/* Security badges */}
      <div className="flex justify-center items-center mt-8 space-x-3">
        <div className="flex items-center text-gray-500 text-sm">
          <Lock size={14} className="mr-1" />
          <span>Login Sicuro</span>
        </div>
        <div className="h-4 border-r border-gray-300"></div>
        <div className="flex items-center text-gray-500 text-sm">
          <CheckCircle size={14} className="mr-1" />
          <span>Privacy Protetta</span>
        </div>
      </div>
    </div>
  );
};


// This component centers the LoginRegistrationForm both horizontally and vertically
const Login = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-beige-light p-4">
      <LoginRegistrationForm />
    </div>
  );
};

export default Login;